/**
 * Helper utility to validate and correct GPS coordinates for Israel
 * Israel's approximate geographic bounds:
 * - Latitude: 29.5° to 33.4° N
 * - Longitude: 34.3° to 35.9° E
 */

export interface Coordinates {
    lat: number;
    lng: number;
}

export interface LocationData {
    Latitude: number;
    Longitude: number;
}

// Israel's approximate geographic bounds
const ISRAEL_BOUNDS = {
    minLatitude: 29.5,
    maxLatitude: 33.4,
    minLongitude: 34.3,
    maxLongitude: 35.9,
};

/**
 * Checks if coordinates are within Israel's geographic bounds
 */
export function isWithinIsraelBounds(lat: number, lng: number): boolean {
    return (
        lat >= ISRAEL_BOUNDS.minLatitude &&
        lat <= ISRAEL_BOUNDS.maxLatitude &&
        lng >= ISRAEL_BOUNDS.minLongitude &&
        lng <= ISRAEL_BOUNDS.maxLongitude
    );
}

/**
 * Validates and corrects coordinates if they appear to be flipped
 * If coordinates are outside Israel bounds, tries flipping lat/lng
 * Returns corrected coordinates or original if they're already valid
 */
export function validateAndCorrectCoordinates(lat: number, lng: number): Coordinates {
    // If coordinates are already within Israel bounds, return as-is
    if (isWithinIsraelBounds(lat, lng)) {
        return { lat, lng };
    }

    // Try flipping the coordinates
    const flippedLat = lng;
    const flippedLng = lat;

    // If flipped coordinates are within Israel bounds, return flipped version
    if (isWithinIsraelBounds(flippedLat, flippedLng)) {
        return { lat: flippedLat, lng: flippedLng };
    }

    // If neither original nor flipped coordinates are within bounds,
    // return original coordinates (might be intentional or from another location)
    return { lat, lng };
}

/**
 * Convenience function that takes a LocationData object and returns corrected coordinates
 */
export function correctLocationData(location: LocationData | null | undefined): Coordinates | null {
    if (!location || typeof location.Latitude !== 'number' || typeof location.Longitude !== 'number') {
        return null;
    }

    if (isNaN(location.Latitude) || isNaN(location.Longitude)) {
        return null;
    }
    return validateAndCorrectCoordinates(location.Latitude, location.Longitude);
}

/**
 * Type guard to check if coordinates are valid numbers
 */
export function areValidCoordinates(lat: any, lng: any): boolean {
    return (
        typeof lat === 'number' &&
        typeof lng === 'number' &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        isFinite(lat) &&
        isFinite(lng)
    );
}
